import { formatDate, formatQuantity, formatUnit } from "@/lib/format-data";

import type { LotNumberHistory } from "@/api/provider/scribe/notes";

export interface LotShipmentDescriptionData {
  expiryDate?: string;
  remainingQuantity: number;
  locationId: string;
  supplierName?: string | null;
  providerAvailableQuantity?: number | null;
  allocatedRemainingQuantity?: number | null;
  unallocatedRemainingQuantity?: number | null;
  chargedTodayQuantity?: number | null;
  chargedTodayChargeCount?: number | null;
}

/** Shared with LotNumberSelectField: do not add separate picker metadata. */
export function getLotHistoryDescription(entry: LotNumberHistory) {
  const parts: string[] = [];
  if (entry.amount != null) {
    parts.push(
      `${formatQuantity(entry.amount)}${entry.unit ? ` ${formatUnit(entry.unit)}` : ""}`,
    );
  }
  if (entry.lastUsedDate) {
    parts.push(`Last used ${formatDate(new Date(entry.lastUsedDate))}`);
  }
  if (entry.doseCount > 1) parts.push(`${entry.doseCount} doses`);
  return parts.length ? parts.join(" · ") : undefined;
}

export function getLotShipmentDescription(
  shipment: LotShipmentDescriptionData,
  locationNames: Record<string, string>,
) {
  const parts: string[] = [];
  if (shipment.expiryDate)
    parts.push(`Exp: ${formatDate(shipment.expiryDate)}`);
  if (shipment.providerAvailableQuantity != null) {
    const providerParts: string[] = [];
    if ((shipment.allocatedRemainingQuantity ?? 0) > 0) {
      providerParts.push(
        `${formatQuantity(shipment.allocatedRemainingQuantity!)} allocated`,
      );
    }
    if ((shipment.unallocatedRemainingQuantity ?? 0) > 0) {
      providerParts.push(
        `${formatQuantity(shipment.unallocatedRemainingQuantity!)} unassigned`,
      );
    }
    parts.push(
      `${formatQuantity(shipment.providerAvailableQuantity)} available to provider${providerParts.length ? ` (${providerParts.join(" + ")})` : ""}`,
    );
  } else {
    parts.push(`${formatQuantity(shipment.remainingQuantity)} available`);
  }
  const locationName = locationNames[shipment.locationId];
  if (locationName) parts.push(locationName);
  if (shipment.supplierName) parts.push(shipment.supplierName);
  if (shipment.chargedTodayQuantity != null) {
    parts.push(
      `${formatQuantity(shipment.chargedTodayQuantity)} charged at checkout`,
    );
    if ((shipment.chargedTodayChargeCount ?? 0) > 1) {
      parts.push(
        `${shipment.chargedTodayChargeCount} checkout charges available`,
      );
    }
  }
  return parts.join(" · ");
}
